import Image from 'next/image';
import { assetSize } from '@/lib/asset-dimensions';
import type { ReactNode } from 'react';

export function SectionHeading({
  eyebrow,
  title,
  lede,
  align = 'left',
}: {
  eyebrow?: string;
  title: ReactNode;
  lede?: string;
  align?: 'left' | 'center';
}) {
  const centered = align === 'center';
  return (
    <div className={centered ? 'mx-auto max-w-2xl text-center' : 'max-w-2xl'}>
      {eyebrow && (
        <p className="eyebrow">
          <span className="h-px w-6 bg-spark-500/70" />
          {eyebrow}
        </p>
      )}
      <h2 className="mt-4 text-balance-tight text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">
        {title}
      </h2>
      {lede && (
        <p className={`mt-4 text-[1.02rem] leading-relaxed text-mist-400 ${centered ? 'mx-auto' : ''}`}>
          {lede}
        </p>
      )}
    </div>
  );
}

/** Product screenshot in a window chrome, so raw PNGs read as real UI. */
export function Shot({
  src,
  alt,
  priority = false,
  className = '',
}: {
  src: string;
  alt: string;
  priority?: boolean;
  className?: string;
}) {
  return (
    <figure className={`shot ${className}`}>
      <div className="flex items-center gap-1.5 border-b border-line bg-ink-800/80 px-3.5 py-2.5">
        {['#ff5f57', '#febc2e', '#28c840'].map((c) => (
          <span key={c} className="h-2.5 w-2.5 rounded-full opacity-60" style={{ background: c }} />
        ))}
        <span className="ml-2 truncate font-mono text-[0.68rem] text-mist-500">
          app.kitagency.co
        </span>
      </div>
      {/* next/image, not <img>: these ship at up to 1900px wide and render
          around 650px, which an audit costs at ~155KiB of wasted download
          across the page. Real dimensions come from the generated map so the
          box is reserved correctly and nothing shifts. */}
      <Image
        src={src}
        alt={alt}
        width={assetSize(src).width}
        height={assetSize(src).height}
        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 90vw, 660px"
        priority={priority}
        loading={priority ? undefined : 'lazy'}
        className="block h-auto w-full"
      />
      <figcaption className="sr-only">{alt}</figcaption>
    </figure>
  );
}

export function Pill({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-line-2 bg-veil-2 px-3.5 py-1.5 text-xs font-medium text-mist-300">
      {children}
    </span>
  );
}

export function Check({ className = '' }: { className?: string }) {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

export function Dash({ className = '' }: { className?: string }) {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M6 12h12" />
    </svg>
  );
}

export function Arrow({ className = '' }: { className?: string }) {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  );
}

/** Page-top band shared by the inner pages. */
export function PageHero({
  eyebrow,
  title,
  lede,
  children,
}: {
  eyebrow: string;
  title: ReactNode;
  lede?: string;
  children?: ReactNode;
}) {
  return (
    <section className="relative overflow-hidden border-b border-line">
      <div className="aurora animate-drift -top-24 left-1/3 h-80 w-80 bg-spark-600/18" />
      <div
        className="aurora animate-drift right-1/4 top-10 h-64 w-64 bg-iris-500/14"
        style={{ animationDelay: '-6s' }}
      />
      <div className="container-page relative py-20 sm:py-28">
        <p className="eyebrow">
          <span className="h-px w-6 bg-spark-500/70" />
          {eyebrow}
        </p>
        <h1 className="mt-5 max-w-3xl text-balance-tight text-4xl font-semibold tracking-[-0.035em] sm:text-5xl md:text-[3.4rem] md:leading-[1.05]">
          {title}
        </h1>
        {lede && (
          <p className="mt-6 max-w-xl text-[1.05rem] leading-relaxed text-mist-400">{lede}</p>
        )}
        {children}
      </div>
    </section>
  );
}
